// client/src/components/admin/AdminOrganizationDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const AdminOrganizationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    vatNumber: '', 
    address: {
      street: '',
      city: '',
      postcode: '',
      country: ''
    },
    isActive: true
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  
  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      try {
        const res = await axios.get(`/api/admin/organizations/${id}`);
        setOrganization(res.data.organization);
        setUsers(res.data.users || []);
        
        setFormData({
          name: res.data.organization.name,
          contactEmail: res.data.organization.contactEmail,
          contactPhone: res.data.organization.contactPhone || '',
          vatNumber: res.data.organization.vatNumber || '',
          address: {
            street: res.data.organization.address?.street || '',
            city: res.data.organization.address?.city || '',
            postcode: res.data.organization.address?.postcode || '',
            country: res.data.organization.address?.country || ''
          },
          isActive: res.data.organization.isActive
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError('Failed to fetch organization details');
        setLoading(false);
      }
    };
    
    fetchOrganizationDetails();
  }, [id]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.replace('address.', '');
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionInProgress(true);
    setError('');
    setSuccess('');
    
    try {
      const res = await axios.put(`/api/admin/organizations/${id}`, formData);
      setSuccess('Organization updated successfully');
      setOrganization(res.data.organization);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update organization');
    } finally {
      setActionInProgress(false);
    }
  };
  
  const toggleOrganizationStatus = async () => {
    setActionInProgress(true);
    setError('');
    setSuccess('');
    
    const newStatus = !formData.isActive;
    
    try {
      await axios.put(`/api/admin/organizations/${id}`, {
        isActive: newStatus
      });
      
      setFormData({
        ...formData,
        isActive: newStatus
      });
      
      setOrganization({
        ...organization,
        isActive: newStatus
      });
      
      setSuccess(`Organization ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} organization`);
    } finally {
      setActionInProgress(false);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading organization details...</div>;
  }
  
  if (error && !organization) {
    return <div className="error-message">{error}</div>;
  }
  
  if (!organization) {
    return <div className="error-message">Organization not found</div>;
  }
  
  return (
    <div className="admin-container">
      <div className="admin-header">
        <button 
          onClick={() => navigate(-1)} 
          className="btn btn-light"
        >
          &larr; Back
        </button>
        <h2 className="admin-page-title">Organization Details</h2>
      </div>
      
      {error && <div className="alert-danger">{error}</div>}
      {success && <div className="alert-success">{success}</div>}
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Organization Information</h3>
          <span className={`status ${organization.isActive ? 'status-approved' : 'status-rejected'}`}>
            {organization.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="name">Organization Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="type">Organization Type</label>
                <input
                  type="text"
                  id="type"
                  value={organization.type === 'bank' ? 'Bank' : 'Company'}
                  className="admin-form-input"
                  disabled
                />
              </div>
            </div>

            {/*VAT Number field - only show for companies */}
            {organization.type === 'company' && (
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="vatNumber">VAT Number</label>
                  <input
                    type="text"
                    id="vatNumber"
                    name="vatNumber"
                    value={formData.vatNumber}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="Enter VAT number"
                  />
                </div>
              </div>
            )}
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="contactEmail">Contact Email</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="contactPhone">Contact Phone</label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="admin-form-input"
                />
              </div>
            </div>
            
            <div className="admin-section-title">Address</div>
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="address.street">Street</label>
                <input
                  type="text"
                  id="address.street"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="address.city">City</label>
                <input
                  type="text"
                  id="address.city"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="address.postcode">Postal Code</label>
                <input
                  type="text"
                  id="address.postcode"
                  name="address.postcode"
                  value={formData.address.postcode}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="address.country">Country</label>
                <input
                  type="text"
                  id="address.country"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group admin-checkbox-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Organization is active
                </label>
              </div>
            </div>
            
            <div className="admin-form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Organization Details */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Organization Details</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-detail-row">
            <div className="admin-detail-label">Organization ID</div>
            <div className="admin-detail-value">{organization.organizationId}</div>
          </div>

          {/*Show VAT Number in read-only details */}
          {organization.type === 'company' && (
          <div className="admin-detail-row">
            <div className="admin-detail-label">VAT Number</div>
            <div className="admin-detail-value">{organization.vatNumber || 'Not specified'}</div>
          </div>
          )}

          {organization.type === 'bank' && organization.totalLiquidity !== undefined && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Total Liquidity</div>
              <div className="admin-detail-value">€{organization.totalLiquidity.toLocaleString()}</div>
            </div>
          )}
          
          {organization.type === 'company' && organization.affiliateBank !== undefined && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Affiliate Bank</div>
              <div className="admin-detail-value">{organization.affiliateBank || 'None'}</div>
            </div>
          )}
          
          <div className="admin-detail-row">
            <div className="admin-detail-label">Registration Date</div>
            <div className="admin-detail-value">
              {new Date(organization.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Organization Users */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Organization Users</h3>
        </div>
        <div className="admin-card-body">
          {users.length === 0 ? (
            <div className="admin-no-data-message">No users found for this organization.</div>
          ) : (
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>{user.position || 'N/A'}</td>
                      <td>
                        <span className={`status ${user.isActive ? 'status-approved' : 'status-rejected'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <Link to={`/admin/users/${user._id}`} className="admin-view-btn">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Activity History */}
      {organization.type === 'company' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Liquidity Requests</h3>
          </div>
          <div className="admin-card-body">
            {/* In real implementation, fetch and display company requests */}
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Sample data - in real implementation, use actual requests */}
                  <tr>
                    <td colSpan="5" className="admin-no-data-message">
                      No requests found for this organization.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {organization.type === 'bank' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Bank Bids</h3>
          </div>
          <div className="admin-card-body">
            {/* In real implementation, fetch and display bank bids */}
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Company</th>
                    <th>Bid Amount</th>
                    <th>Interest Rate</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Sample data - in real implementation, use actual bids */}
                  <tr>
                    <td colSpan="6" className="admin-no-data-message">
                      No bids found for this bank.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Actions</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-actions-grid">
            <button
              className={`btn ${formData.isActive ? 'btn-danger' : 'btn-primary'}`}
              onClick={toggleOrganizationStatus}
              disabled={actionInProgress}
            >
              {formData.isActive ? 'Deactivate Organization' : 'Activate Organization'}
            </button>
            
            <button
              className="btn btn-light"
              onClick={() => navigate('/admin/organizations')}
              disabled={actionInProgress}
            >
              Back to Organization List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrganizationDetails;